import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@mui/material';
import { UpdateInvoiceAdditionalDetailsRequestSchema, type UpdateInvoiceAdditionalDetailsRequestPayload } from '@weft/api-definition';
import { isNotNullOrUndefined } from '@weft/utils';
import VyDialog from 'apps/app/src/components/new-design/feedback/VyDialog';
import { useUpdateInvoiceAdditionalDetails } from 'apps/app/src/hooks/requests/use-update-invoice-additional-details';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient, type QueryKey } from 'react-query';

type AddInvoicePurchaseOrderFormProps = {
  open: boolean;
  onClose?: () => void;
  queryKeyToInvalidate?: QueryKey
  existingPurchaseOrder?: string;
  invoiceId: string;
};

export const AddInvoicePurchaseOrderForm = ({
  open,
  onClose,
  existingPurchaseOrder,
  invoiceId,
  queryKeyToInvalidate,
}: AddInvoicePurchaseOrderFormProps) => {
  const updatePurchaseOrderForm = useForm<UpdateInvoiceAdditionalDetailsRequestPayload>({
    resolver: zodResolver(UpdateInvoiceAdditionalDetailsRequestSchema),
    defaultValues: {
      purchaseOrder: existingPurchaseOrder,
      id: invoiceId,
    },
  });

  const purchaseOrderInputRegister = updatePurchaseOrderForm.register('purchaseOrder');

  const queryClient = useQueryClient();
  const updateInvoiceAdditionalDetailsMutation = useUpdateInvoiceAdditionalDetails();
  
  const onSubmit = updatePurchaseOrderForm.handleSubmit(async ({ purchaseOrder, id }) => {
    await runMutation(
      updateInvoiceAdditionalDetailsMutation,
      { id, purchaseOrder },
      {
        pending: 'Updating purchase order...',
        success: 'Purchase order updated',
        error: 'Failed to update purchase order',
      },
      {
        onSuccess: () => queryClient.invalidateQueries(queryKeyToInvalidate),
      },
    );

    return onClose?.();
  });

  const handleClose = () => {
    updatePurchaseOrderForm.reset();
    updatePurchaseOrderForm.clearErrors();
    onClose?.();
  };

  return (
    <>
      <VyDialog
        open={open}
        onClose={handleClose}
        label="Add purchase order number"
        labelDescription="The PO number will be added to this invoice only"
        onSubmit={onSubmit}
        ctaText="Update"
        cancelButtonText="Cancel"
      >
        <TextField
          {...purchaseOrderInputRegister}
          label="PO number"
          placeholder="Text here"
          size='medium'
          fullWidth
          sx={{
            padding: '15px',
          }}
          error={isNotNullOrUndefined(updatePurchaseOrderForm.formState.errors[purchaseOrderInputRegister.name])}
          helperText={updatePurchaseOrderForm.formState.errors[purchaseOrderInputRegister.name]?.message}
        />
      </VyDialog>
    </>
  );
};
